import React, { useState, useMemo, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { grievanceService } from '../api/grievanceService';
import * as XLSX from 'xlsx';
import './Dashboard.css';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/* ─────────────────────────────────────────────────────────────
   PS_BY_LEVEL  — L-1 through L-6 (Kanpur Police Master Data)
───────────────────────────────────────────────────────────── */
const PS_BY_LEVEL = {
  'L-1': [
'AHTU','Anwarganj','Armapur','Araul','Babupurwa','Badshahi Naka','Bajaria','Bara','Bekanganj','Bidhnu','Bilhaur',
'Bithoor','Chakeri','Chamanganj','Chaubepur','Chhawani','Collectorganj','Colonelganj','Cyber Thana /Cyber Crime Cell','Fazalganj',
'Ghatampur','Govindnagar','Gujaini','Gwaltoli','Hanumant Vihar','Harbansh Mohal','Jajmau','Juhi','Kakadeo','Kakwan',
'Kalyanpur','Kidwainagar','Kohna','Kotwali','Maharajpur','Mahila Thana','Moolganj','Narwal','Nawabganj','Naubasta','Nazeerabad','Panki','Pheelkhana','Rail Bazar',
'Raipurwa','Rawatpur','Reuna','Saadh','Sachendi','Sajeti','Sen Paschim Para','Shivrajpur','Sisamau','Swaroopnagar'
  ],
  'L-2': [
    'ACP Kotwali', 'ACP Collectorganj', 'ACP Cantt', 'ACP Chakeri', 'ACP Crime',
    'ACP Panki', 'ACP Kalyanpur', 'ACP Bilhaur', 'ACP Babupurwa', 'ACP Naubasta',
    'ACP Ghatampur', 'ACP Colonelganj', 'ACP Sisamau', 'ACP Anwarganj',
    'ACP Swaroop Nagar'
  ],
  'L-3': ['ADCP East', 'ADCP West', 'ADCP South', 'ADCP Central', 'ADCP Crime', 'ADCP Traffic', 'ADCP CAW', 'ADCP Line'],
  'L-4': ['DCP Traffic', 'DCP HQ', 'DCP East', 'DCP West', 'DCP South', 'DCP Central', 'DCP Crime'],
  'L-5': [
    'Additional Commissioner of Police (Law & Order)',
    'Additional Commissioner of Police (Crime/HQ)'
  ],
  'L-6': ['Commissioner of Police']
};

const LEVEL_ORDER = ['L-1', 'L-2', 'L-3', 'L-4', 'L-5', 'L-6'];

const LEVEL_LABELS = {
  'L-1': 'Police Station',
  'L-2': 'ACP',
  'L-3': 'ADCP',
  'L-4': 'DCP',
  'L-5': 'Addl. Commissioner',
  'L-6': 'Commissioner of Police'
};

/* ─────────────────────────────────────────────────────────────
   helpers
───────────────────────────────────────────────────────────── */
function statusColor(s) {
  if (s === 'Satisfied')    return '#059669';
  if (s === 'Not Satisfied') return '#dc2626';
  if (s === 'Miscellaneous') return '#7c3aed';
  return '#d97706';
}
function statusIcon(s) {
  if (s === 'Satisfied')    return '✅';
  if (s === 'Not Satisfied') return '❌';
  if (s === 'Miscellaneous') return '📌';
  return '⏳';
}

function extractStatus(action) {
  if (!action) return null;
  const m = action.match(/satisfied|pending|miscellaneous|not satisfied/i);
  if (!m) return null;
  const s = m[0].toLowerCase();
  if (s === 'satisfied') return 'Satisfied';
  if (s === 'not satisfied') return 'Not Satisfied';
  if (s === 'miscellaneous') return 'Miscellaneous';
  if (s === 'pending') return 'Pending';
  return null;
}

/* ─────────────────────────────────────────────────────────────
   History Timeline Popup  —  FIXED to show all intermediate levels
───────────────────────────────────────────────────────────── */
function HistoryTimelinePopup({ complaint, statusHistory = [], onClose }) {
  if (!complaint) return null;

  const events = [];

  // 1. First Receipt — always first
  events.push({
  key: 'first-receipt',
  label: 'Current Position',
  date: complaint.lastMarkedDate
    ? new Date(complaint.lastMarkedDate).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : complaint.firstReceiptDate
      ? new Date(complaint.firstReceiptDate).toLocaleString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : 'N/A',

  subLabel: complaint.currentPS || '—',
  extra: `Level: ${complaint.level || 'N/A'}`,
  color: '#059669',
  icon: '📍',
  ts: complaint.lastMarkedDate
    ? new Date(complaint.lastMarkedDate).getTime()
    : complaint.firstReceiptDate
      ? new Date(complaint.firstReceiptDate).getTime()
      : 0
});

  // 2. Backend history entries OR synthesised fallback
  if (complaint.history && complaint.history.length > 0) {
    // ── Use real backend history ──
    complaint.history.forEach((h, idx) => {
      const ts = h.created_at ? new Date(h.created_at).getTime() : 0;

      const isStatusChange = h.new_status || (h.action && /satisfied|pending|miscellaneous|not satisfied/i.test(h.action));
      const newStatus = h.new_status || (isStatusChange ? extractStatus(h.action) : null);

      let color = '#0369a1';
      let icon  = '🔄';
      let label = h.action || 'Action';

      if (isStatusChange && newStatus) {
        color = statusColor(newStatus);
        icon  = statusIcon(newStatus);
        label = `Status → ${newStatus}`;
      }

      const subParts = [];
      if (h.from_ps) subParts.push(`From: ${h.from_ps}`);
      if (h.to_ps)   subParts.push(`→ ${h.to_ps}`);

      events.push({
        key: `backend-${idx}`,
        label,
        date: h.created_at
          ? new Date(h.created_at).toLocaleString('en-IN', {
              day: 'numeric', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit'
            })
          : '',
        subLabel: subParts.join(' '),
        extra: h.action_by ? `By: ${h.action_by}` : '',
        color,
        icon,
        ts
      });
    });

  } else {
    // ── Fallback: reconstruct full escalation chain from flat fields ──
    const fromLevel    = complaint.forwarded_from_level || complaint.forwardedFromLevel;
    const fromPS       = complaint.previous_ps || complaint.previousPS;
    const currentLevel = complaint.level;

    const currentLevelIdx = LEVEL_ORDER.indexOf(currentLevel);
    const fromIdx         = LEVEL_ORDER.indexOf(fromLevel);

    const baseTs = complaint.firstReceiptDate
      ? new Date(complaint.firstReceiptDate).getTime()
      : Date.now();

    if (fromLevel && fromIdx >= 0 && currentLevelIdx > 0) {
      // Add a node for every level that was passed through before the known "from" level
      // e.g. if current=L-5, fromLevel=L-4, we add L-1, L-2, L-3 as intermediate hops
      for (let i = 0; i < fromIdx; i++) {
        const lvl      = LEVEL_ORDER[i];
        const nextLvl  = LEVEL_ORDER[i + 1];
        events.push({
          key: `synthetic-hop-${i}`,
          label: `Escalated: ${lvl} → ${nextLvl}`,
          date: complaint.firstReceiptDate
            ? new Date(complaint.firstReceiptDate).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'short', year: 'numeric'
              })
            : '',
          subLabel: `${LEVEL_LABELS[lvl] || lvl} → ${LEVEL_LABELS[nextLvl] || nextLvl}`,
          extra: '',
          color: '#0369a1',
          icon: '↗️',
          ts: baseTs + i + 1
        });
      }

      // Add the last known forwarding hop (fromLevel → currentLevel)
      const subParts = [];
      if (fromPS)              subParts.push(`From: ${fromPS}`);
      if (fromLevel)           subParts.push(`(${fromLevel})`);
      if (complaint.currentPS) subParts.push(`→ ${complaint.currentPS}`);

      events.push({
        key: 'synthetic-escalation',
        label: `Escalated: ${fromLevel} → ${currentLevel}`,
        date: complaint.firstReceiptDate
          ? new Date(complaint.firstReceiptDate).toLocaleDateString('en-IN', {
              day: 'numeric', month: 'short', year: 'numeric'
            })
          : '',
        subLabel: subParts.join(' '),
        extra: `To level: ${currentLevel || 'N/A'}`,
        color: '#0369a1',
        icon: '↗️',
        ts: baseTs + fromIdx + 1
      });

    } else if (fromLevel || fromPS) {
      // Simpler case: only one hop known
      const subParts = [];
      if (fromPS)              subParts.push(`From: ${fromPS}`);
      if (fromLevel)           subParts.push(`(${fromLevel})`);
      if (complaint.currentPS) subParts.push(`→ ${complaint.currentPS}`);

      events.push({
        key: 'synthetic-escalation',
        label: 'Escalated / Forwarded',
        date: complaint.firstReceiptDate
          ? new Date(complaint.firstReceiptDate).toLocaleDateString('en-IN', {
              day: 'numeric', month: 'short', year: 'numeric'
            })
          : '',
        subLabel: subParts.join(' '),
        extra: `To level: ${currentLevel || 'N/A'}`,
        color: '#0369a1',
        icon: '↗️',
        ts: baseTs + 1
      });
    }

    // Synthesise a status node if status is not Pending
    if (complaint.status && complaint.status !== 'Pending') {
      const statusTs = complaint.lastMarkedDate
        ? new Date(complaint.lastMarkedDate).getTime()
        : complaint.updatedAt
          ? new Date(complaint.updatedAt).getTime()
          : Date.now();

      events.push({
        key: 'synthetic-status',
        label: `Status → ${complaint.status}`,
        date: complaint.lastMarkedDate
          ? new Date(complaint.lastMarkedDate).toLocaleString('en-IN', {
              day: 'numeric', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit'
            })
          : '',
        subLabel: complaint.remarks ? `Remarks: ${complaint.remarks}` : '',
        extra: '',
        color: statusColor(complaint.status),
        icon: statusIcon(complaint.status),
        ts: statusTs
      });
    }
  }

  // 3. Frontend status-change history (every Save Changes click)
  statusHistory.forEach((sh, idx) => {
    events.push({
      key: `status-${idx}`,
      label: `Status → ${sh.status}`,
      date: new Date(sh.ts).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      }),
      subLabel: sh.remarks ? `Remarks: ${sh.remarks}` : '',
      extra: '',
      color: statusColor(sh.status),
      icon:  statusIcon(sh.status),
      ts: sh.ts
    });
  });

  // Sort all events chronologically
  events.sort((a, b) => a.ts - b.ts);

  // Deduplicate consecutive identical events within 2 seconds
  const deduped = events.reduce((acc, ev) => {
    if (acc.length === 0) return [ev];
    const prev = acc[acc.length - 1];
    const sameLabel   = prev.label === ev.label;
    const closeInTime = Math.abs(prev.ts - ev.ts) < 2000;
    if (sameLabel && closeInTime) return acc;
    return [...acc, ev];
  }, []);

  return (
    <div className="timeline-overlay" onClick={onClose}>
      <div className="timeline-popup" onClick={(e) => e.stopPropagation()}>

        <div className="timeline-popup-header">
          <div className="timeline-popup-title">
            <span>🕐</span>
            <span>History Timeline — Complaint #{complaint.id}</span>
          </div>
          <button className="timeline-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="timeline-info-strip">
          <span><strong>Name:</strong> {complaint.name}</span>
          <span><strong>Mobile:</strong> {complaint.mobile}</span>
          <span><strong>Level:</strong> {complaint.level}</span>
        </div>

        <div className="timeline-flow-container">
          {deduped.map((node, idx) => (
            <div key={node.key} className="timeline-flow-step">
              <div className="timeline-node" style={{ borderColor: node.color }}>
                <div className="timeline-node-header" style={{ backgroundColor: node.color }}>
                  <span className="timeline-node-icon">{node.icon}</span>
                  <span className="timeline-node-label">{node.label}</span>
                </div>
                <div className="timeline-node-body">
                  {node.date     && <div className="timeline-node-date">{node.date}</div>}
                  {node.subLabel && <div className="timeline-node-sub">{node.subLabel}</div>}
                  {node.extra    && <div className="timeline-node-by">{node.extra}</div>}
                </div>
              </div>
              {idx < deduped.length - 1 && (
                <div className="timeline-arrow-wrapper">
                  <div className="timeline-arrow-line"></div>
                  <div className="timeline-arrow-head">▼</div>
                </div>
              )}
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Remarks Expand Popup
───────────────────────────────────────────────────────────── */
function RemarksExpandPopup({ title, value, onClose }) {
  return (
    <div className="remarks-expand-overlay" onClick={onClose}>
      <div className="remarks-expand-popup" onClick={(e) => e.stopPropagation()}>
        <div className="remarks-expand-header">
          <span className="remarks-expand-title">{title}</span>
          <button className="remarks-expand-close" onClick={onClose}>✕</button>
        </div>
        <div className="remarks-expand-body">
          {value && value.trim()
            ? <p className="remarks-expand-text">{value}</p>
            : <p className="remarks-expand-empty">No remarks entered yet.</p>}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Dashboard
───────────────────────────────────────────────────────────── */
function Dashboard() {
  const { user } = useContext(AuthContext);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [activeTab, setActiveTab]   = useState('Pending');
  const [selectedId, setSelectedId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter]   = useState({ startDate: '', endDate: '', month: '', year: '' });
  const [levelFilter, setLevelFilter] = useState('');
  const [psFilter, setPsFilter]       = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => { fetchGrievances(); }, []);

  const fetchGrievances = async () => {
    try {
      setLoading(true);
      const response = await grievanceService.getAllGrievances();
      if (response.success) {
        let grievancesData = [];
        if (response.data && Array.isArray(response.data.data)) grievancesData = response.data.data;
        else if (Array.isArray(response.data)) grievancesData = response.data;

        const transformedData = grievancesData.map(g => ({
          id: g.id,
          level: g.level,
          firstReceiptDate: g.first_receipt_date,
          lastMarkedDate: g.last_marked_date,
          name: g.name,
          mobile: g.mobile,
          description: g.description,
          currentPS: g.current_ps,
          status: g.status,
          remarks: g.remarks || '',
          history: g.history || [],
          statusHistory: [],
          createdAt: g.created_at || g.createdAt,
          updatedAt: g.updated_at || g.updatedAt,
          forwarded_from_level: g.forwarded_from_level,
          forwardedFromLevel: g.forwarded_from_level,
          previous_ps: g.previous_ps,
          previousPS: g.previous_ps,
          enquiryOfficerName: g.enquiry_officer_name || g.enquiryOfficerName || ''
        }));
        setComplaints(transformedData);
      }
    } catch (err) {
      console.error('Error fetching grievances:', err);
      setError('Failed to load grievances');
    } finally {
      setLoading(false);
    }
  };

  /* ── Download helpers ── */
  const downloadAllComplaintsExcel = () => {
    setIsDownloading(true);
    try {
      const wb = XLSX.utils.book_new();
      const categories = {
        'Pending':       complaints.filter(c => c.status === 'Pending'),
        'Satisfied':     complaints.filter(c => c.status === 'Satisfied'),
        'Not Satisfied': complaints.filter(c => c.status === 'Not Satisfied'),
        'Miscellaneous': complaints.filter(c => c.status === 'Miscellaneous')
      };
      Object.keys(categories).forEach(category => {
        const excelData = categories[category].map(c => ({
          'Sr. No': c.id, 'Level': c.level,
          'First Receipt Date': c.firstReceiptDate ? new Date(c.firstReceiptDate).toLocaleDateString('en-IN') : '',
          'Last Marked Date': c.lastMarkedDate ? new Date(c.lastMarkedDate).toLocaleDateString('en-IN') : '',
          'Name': c.name, 'Mobile': c.mobile, 'Description': c.description,
          'Current Police Station': c.currentPS, 'Status': c.status,
          'Enquiry Officer Name': c.enquiryOfficerName || '',
          'Created At': c.createdAt ? new Date(c.createdAt).toLocaleString('en-IN') : '',
          'Updated At': c.updatedAt ? new Date(c.updatedAt).toLocaleString('en-IN') : ''
        }));
        if (excelData.length === 0) excelData.push({ 'Sr. No': 'No Data' });
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [8,6,15,15,20,12,40,30,12,30,30,20,20].map(w => ({ wch: w }));
        XLSX.utils.book_append_sheet(wb, ws, category);
      });
      const summaryWs = XLSX.utils.aoa_to_sheet([
        ['Complaints Summary Report'],
        ['Generated on', new Date().toLocaleString('en-IN')], [''],
        ['Category', 'Count'],
        ['Pending', categories['Pending'].length],
        ['Satisfied', categories['Satisfied'].length],
        ['Not Satisfied', categories['Not Satisfied'].length],
        ['Miscellaneous', categories['Miscellaneous'].length],
        [''], ['Total Complaints', complaints.length]
      ]);
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
      XLSX.writeFile(wb, `Complaints_Report_${new Date().toISOString().slice(0,19).replace(/[:]/g,'-')}.xlsx`);
      alert(`Excel downloaded! Total: ${complaints.length}`);
    } catch (err) { alert('Failed to download Excel. Please try again.'); }
    finally { setIsDownloading(false); }
  };

  const downloadFilteredComplaintsExcel = () => {
    setIsDownloading(true);
    try {
      const wb = XLSX.utils.book_new();
      const excelData = filtered.map(c => ({
        'Sr. No': c.id, 'Level': c.level,
        'First Receipt Date': c.firstReceiptDate ? new Date(c.firstReceiptDate).toLocaleDateString('en-IN') : '',
        'Last Marked Date': c.lastMarkedDate ? new Date(c.lastMarkedDate).toLocaleDateString('en-IN') : '',
        'Name': c.name, 'Mobile': c.mobile, 'Description': c.description,
        'Current Police Station': c.currentPS, 'Status': c.status,
        'Enquiry Officer Name': c.enquiryOfficerName || '',
        'Created At': c.createdAt ? new Date(c.createdAt).toLocaleString('en-IN') : '',
        'Updated At': c.updatedAt ? new Date(c.updatedAt).toLocaleString('en-IN') : ''
      }));
      if (excelData.length === 0) excelData.push({ 'Sr. No': 'No Data' });
      const ws = XLSX.utils.json_to_sheet(excelData);
      ws['!cols'] = [8,6,15,15,20,12,40,30,12,30,30,20,20].map(w => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, ws, `${activeTab}_Complaints`);
      XLSX.writeFile(wb, `${activeTab}_Complaints_${new Date().toISOString().slice(0,19).replace(/[:]/g,'-')}.xlsx`);
      alert(`Downloaded ${filtered.length} ${activeTab} complaints!`);
    } catch (err) { alert('Failed to download Excel. Please try again.'); }
    finally { setIsDownloading(false); }
  };

  /* ── Filtering ── */
  const filtered = useMemo(() => {
    let result = complaints.filter((c) => c.status === activeTab);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(q) || c.mobile.includes(q) ||
        c.description.toLowerCase().includes(q) || c.currentPS.toLowerCase().includes(q) ||
        c.id.toString().includes(q)
      );
    }
    if (levelFilter) result = result.filter(c => c.level === levelFilter);
    if (psFilter)    result = result.filter(c => c.currentPS === psFilter);
    if (dateFilter.startDate) { const d = new Date(dateFilter.startDate); d.setHours(0,0,0,0); result = result.filter(c => new Date(c.firstReceiptDate) >= d); }
    if (dateFilter.endDate)   { const d = new Date(dateFilter.endDate);   d.setHours(23,59,59,999); result = result.filter(c => new Date(c.firstReceiptDate) <= d); }
    if (dateFilter.month) result = result.filter(c => new Date(c.firstReceiptDate).getMonth() + 1 === parseInt(dateFilter.month));
    if (dateFilter.year)  result = result.filter(c => new Date(c.firstReceiptDate).getFullYear() === parseInt(dateFilter.year));
    return result;
  }, [complaints, activeTab, searchQuery, levelFilter, psFilter, dateFilter]);

  const selected = complaints.find((c) => c.id === selectedId) || null;
  const counts   = complaints.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});

  const chartData = useMemo(() => {
    const total = complaints.length;
    if (total === 0) return { miscellaneous: 0, pending: 0, satisfied: 0, notSatisfied: 0 };
    return {
      miscellaneous: ((counts['Miscellaneous'] || 0) / total * 100).toFixed(1),
      pending:       ((counts['Pending']       || 0) / total * 100).toFixed(1),
      satisfied:     ((counts['Satisfied']     || 0) / total * 100).toFixed(1),
      notSatisfied:  ((counts['Not Satisfied'] || 0) / total * 100).toFixed(1)
    };
  }, [counts, complaints.length]);

  const barChartData = {
    labels: ['Misc', 'Pending', 'Satisfied', 'Not Satisfied'],
    datasets: [{
      label: 'Percentage (%)',
      data: [parseFloat(chartData.miscellaneous), parseFloat(chartData.pending), parseFloat(chartData.satisfied), parseFloat(chartData.notSatisfied)],
      backgroundColor: ['rgba(255,99,132,0.7)','rgba(255,206,86,0.7)','rgba(75,192,192,0.7)','rgba(54,162,235,0.7)'],
      borderColor:     ['rgba(255,99,132,1)',   'rgba(255,206,86,1)',  'rgba(75,192,192,1)',  'rgba(54,162,235,1)'],
      borderWidth: 1,
    }],
  };
  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: true, text: 'Complaint Status (%)', font: { size: 14, weight: 'bold' }, padding: { top: 8, bottom: 20 } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%` } }
    },
    scales: {
      y: { beginAtZero: true, max: 100, ticks: { callback: (v) => v + '%' } },
      x: {}
    },
    maintainAspectRatio: false
  };

  /* ── updateComplaintStatus ── */
  const updateComplaintStatus = async (id, patch) => {
    try {
      if (patch.level && patch.currentPS) {
        await grievanceService.escalateGrievance(id, patch.level, patch.currentPS);
      } else {
        await grievanceService.updateGrievanceStatus(id, patch.status, patch.remarks);
      }

      await fetchGrievances();

      setComplaints(prev => prev.map(c => {
        if (c.id !== id) return c;
        const newHistoryEntry = { status: patch.status, remarks: patch.remarks || '', ts: Date.now() };
        const prevComplaint = prev.find(x => x.id === id);
        const existingStatusHistory = prevComplaint?.statusHistory || [];
        return { ...c, ...patch, statusHistory: [...existingStatusHistory, newHistoryEntry] };
      }));

    } catch (err) {
      console.error('Error updating grievance:', err);
      alert('Failed to update grievance. Please try again.');
    }
  };

  const clearFilters = () => { setSearchQuery(''); setDateFilter({ startDate:'', endDate:'', month:'', year:'' }); setLevelFilter(''); setPsFilter(''); };
  const availableYears = useMemo(() => { const y = new Set(); complaints.forEach(c => y.add(new Date(c.firstReceiptDate).getFullYear())); return Array.from(y).sort((a,b)=>b-a); }, [complaints]);
  const availablePS    = useMemo(() => levelFilter ? (PS_BY_LEVEL[levelFilter] || []) : Object.values(PS_BY_LEVEL).flat(), [levelFilter]);

  if (loading) return <div className="dashboard-wrapper"><div style={{textAlign:'center',padding:'2rem'}}><div className="spinner"></div><p>Loading grievances...</p></div></div>;
  if (error)   return <div className="dashboard-wrapper"><div style={{textAlign:'center',padding:'2rem',color:'red'}}><p>{error}</p><button onClick={fetchGrievances}>Retry</button></div></div>;

  return (
    <div className="dashboard-wrapper">
      <main className="dashboard-main">
        <aside className="sidebar-left">
          <Sidebar
            activeTab={activeTab} setActiveTab={setActiveTab} user={user} counts={counts}
            chartData={chartData} barChartData={barChartData} barChartOptions={barChartOptions}
            onDownloadAll={downloadAllComplaintsExcel} onDownloadCurrent={downloadFilteredComplaintsExcel}
            isDownloading={isDownloading} totalCount={complaints.length} filteredCount={filtered.length}
          />
        </aside>
        <section className="complaints-section">
          <SearchFilters
            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            dateFilter={dateFilter}   setDateFilter={setDateFilter}
            levelFilter={levelFilter} setLevelFilter={setLevelFilter}
            psFilter={psFilter}       setPsFilter={setPsFilter}
            availableYears={availableYears} availablePS={availablePS}
            clearFilters={clearFilters}
            filteredCount={filtered.length}
            totalCount={complaints.filter(c => c.status === activeTab).length}
          />
          <ComplaintList complaints={filtered} onSelect={setSelectedId} selectedId={selectedId} />
        </section>
        <aside className="details-panel">
          <DetailPanel complaint={selected} onUpdate={updateComplaintStatus} user={user} />
        </aside>
      </main>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Search Filters
───────────────────────────────────────────────────────────── */
function SearchFilters({ searchQuery, setSearchQuery, dateFilter, setDateFilter, levelFilter, setLevelFilter, psFilter, setPsFilter, availableYears, availablePS, clearFilters, filteredCount, totalCount }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const hasActiveFilters = searchQuery || dateFilter.startDate || dateFilter.endDate || dateFilter.month || dateFilter.year || levelFilter || psFilter;

  return (
    <div className="search-filters-container">
      <div className="search-bar">
        <input type="text" placeholder="Search by name, mobile, description, PS, or ID..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
        <button className="toggle-filters-btn" onClick={() => setShowAdvanced(!showAdvanced)}>{showAdvanced ? '▲ Hide Filters' : '▼ Advanced Filters'}</button>
      </div>
      {showAdvanced && (
        <div className="advanced-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>Level</label>
              <select value={levelFilter} onChange={(e) => { setLevelFilter(e.target.value); setPsFilter(''); }} className="filter-select">
                <option value="">All Levels</option>
                <option value="L-1">L-1 (Police Stations)</option>
                <option value="L-2">L-2 (ACP)</option>
                <option value="L-3">L-3 (ADCP)</option>
                <option value="L-4">L-4 (DCP)</option>
                <option value="L-5">L-5 (Addl. Commissioner)</option>
                <option value="L-6">L-6 (Commissioner of Police)</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Officer / Police Station</label>
              <select value={psFilter} onChange={(e) => setPsFilter(e.target.value)} className="filter-select">
                <option value="">All</option>
                {availablePS.map((ps) => <option key={ps} value={ps}>{ps}</option>)}
              </select>
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-group"><label>Start Date</label><input type="date" value={dateFilter.startDate} onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })} className="filter-input" /></div>
            <div className="filter-group"><label>End Date</label>  <input type="date" value={dateFilter.endDate}   onChange={(e) => setDateFilter({ ...dateFilter, endDate:   e.target.value })} className="filter-input" /></div>
          </div>
          <div className="filter-row">
            <div className="filter-group">
              <label>Month</label>
              <select value={dateFilter.month} onChange={(e) => setDateFilter({ ...dateFilter, month: e.target.value })} className="filter-select">
                <option value="">All Months</option>
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m,i) => <option key={m} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Year</label>
              <select value={dateFilter.year} onChange={(e) => setDateFilter({ ...dateFilter, year: e.target.value })} className="filter-select">
                <option value="">All Years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          {hasActiveFilters && <div className="filter-actions"><button className="clear-filters-btn" onClick={clearFilters}>Clear All Filters</button></div>}
        </div>
      )}
      <div className="filter-results">
        Showing {filteredCount} of {totalCount} complaints
        {hasActiveFilters && <span className="filter-active-indicator"> (Filtered)</span>}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Sidebar
───────────────────────────────────────────────────────────── */
function Sidebar({ activeTab, setActiveTab, user, counts, chartData, barChartData, barChartOptions, onDownloadAll, onDownloadCurrent, isDownloading, totalCount, filteredCount }) {
  return (
    <div className="sidebar-box">
      <div className="tab-row">
        <button className={`tab-btn ${activeTab==='Pending'   ?'active':''}`} onClick={() => setActiveTab('Pending')}>Pending</button>
        <button className={`tab-btn ${activeTab==='Satisfied' ?'active':''}`} onClick={() => setActiveTab('Satisfied')}>Satisfied</button>
      </div>
      <div className="tab-row">
        <button className={`tab-btn ${activeTab==='Not Satisfied' ?'active':''}`} onClick={() => setActiveTab('Not Satisfied')}>Not Satisfied</button>
        <button className={`tab-btn ${activeTab==='Miscellaneous' ?'active':''}`} onClick={() => setActiveTab('Miscellaneous')}>Miscellaneous</button>
      </div>

      <div className="chart-section">
        <div className="chart-header"><span className="chart-icon">📊</span><span className="chart-title">Complaint Distribution</span></div>
        <div className="chart-container"><Bar data={barChartData} options={barChartOptions} /></div>
        <div className="chart-percentages">
          <div className="percentage-row"><span className="percentage-label">Miscellaneous:</span><span className="percentage-value">{chartData.miscellaneous}%</span></div>
          <div className="percentage-row"><span className="percentage-label">Pending:</span>      <span className="percentage-value">{chartData.pending}%</span></div>
          <div className="percentage-row"><span className="percentage-label">Satisfied:</span>    <span className="percentage-value">{chartData.satisfied}%</span></div>
          <div className="percentage-row"><span className="percentage-label">Not Satisfied:</span><span className="percentage-value">{chartData.notSatisfied}%</span></div>
        </div>
      </div>

      <div className="download-section">
        <div className="download-header"><span className="download-icon">📥</span><span className="download-title">Export Data</span></div>
        <button className={`download-btn ${isDownloading?'downloading':''}`} onClick={onDownloadCurrent} disabled={isDownloading}>{isDownloading?'Downloading...':`Download ${activeTab} (${filteredCount})`}</button>
        <button className={`download-btn download-all ${isDownloading?'downloading':''}`} onClick={onDownloadAll} disabled={isDownloading}>{isDownloading?'Downloading...':`Download All (${totalCount})`}</button>
        <div className="download-info">
          <small>• Current tab: Downloads only {activeTab} complaints</small>
          <small>• All: Downloads 4 separate sheets with Summary</small>
        </div>
      </div>

      <div className="stats-section">
        <div className="stats-label">Quick Stats</div>
        <div className="stats-grid">
          <div className="stat-box stat-total">      <div className="stat-value">{totalCount}</div>                    <div className="stat-label">Total</div></div>
          <div className="stat-box stat-pending">    <div className="stat-value">{counts['Pending']       || 0}</div> <div className="stat-label">Pending</div></div>
          <div className="stat-box stat-satisfied">  <div className="stat-value">{counts['Satisfied']     || 0}</div> <div className="stat-label">Satisfied</div></div>
          <div className="stat-box stat-notsatisfied"><div className="stat-value">{counts['Not Satisfied'] || 0}</div> <div className="stat-label">Not Satisfied</div></div>
          <div className="stat-box stat-misc">       <div className="stat-value">{counts['Miscellaneous'] || 0}</div> <div className="stat-label">Misc</div></div>
          <div className="stat-box stat-active">     <div className="stat-value">{counts[activeTab]       || 0}</div> <div className="stat-label">{activeTab}</div></div>
        </div>
      </div>

      <div className="note-text">
        L-1: PS &nbsp;·&nbsp; L-2: ACP &nbsp;·&nbsp; L-3: ADCP &nbsp;·&nbsp; L-4: DCP &nbsp;·&nbsp; L-5: Addl. CP &nbsp;·&nbsp; L-6: CP
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Complaint List
───────────────────────────────────────────────────────────── */
function ComplaintList({ complaints, onSelect, selectedId }) {
  return (
    <div className="complaint-list-box">
      <h3 className="section-title">Complaints</h3>
      {complaints.length === 0 && <div className="no-complaints">No complaints match the current filters.</div>}
      <div className="complaint-cards">
        {complaints.map((c) => (
          <div key={c.id} className={`complaint-card ${selectedId===c.id?'selected':''}`} onClick={() => onSelect(c.id)}>
            <div className="card-header">
              <div className="card-name">{c.name}</div>
              <div className={`status-badge status-${c.status.replace(' ','-').toLowerCase()}`}>{c.status}</div>
              <div className="card-date">{new Date(c.firstReceiptDate).toLocaleDateString()}</div>
            </div>
            <div className="card-description">{c.description}</div>
            <div className="card-meta">
              <span className="level-badge">Level: {c.level}</span>
              <span>PS: {c.currentPS}</span>
              <span>📱 {c.mobile}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Detail Panel
───────────────────────────────────────────────────────────── */
function DetailPanel({ complaint, onUpdate, user }) {
  const [remarks, setRemarks]                       = useState('');
  const [status, setStatus]                         = useState('Pending');
  const [escalateTo, setEscalateTo]                 = useState('');
  const [escalatePS, setEscalatePS]                 = useState('');

  const [showTimeline, setShowTimeline]             = useState(false);
  const [showRemarksExpand, setShowRemarksExpand]   = useState(false);

  useEffect(() => {
    setRemarks(complaint?.remarks || '');
    setStatus(complaint?.status || 'Pending');
    setEscalateTo(''); setEscalatePS('');
    setShowTimeline(false); setShowRemarksExpand(false);
  }, [complaint]);

  function getHigherLevels(current) {
    const idx = LEVEL_ORDER.indexOf(current);
    return idx < 0 ? [] : LEVEL_ORDER.slice(idx + 1);
  }

  function save() {
    if (!complaint) return;
    const patch = { remarks, status };
    if (status === 'Not Satisfied' && escalateTo && escalatePS) { patch.level = escalateTo; patch.currentPS = escalatePS; }
    onUpdate(complaint.id, patch);
  }

  if (!complaint)
    return (
      <div className="detail-box detail-box--empty">
        <p className="select-message">Select a complaint to view details.</p>
      </div>
    );

  const higherLevels     = getHigherLevels(complaint.level);
  const escalateStations = escalateTo ? PS_BY_LEVEL[escalateTo] || [] : [];

  return (
    <>
      <div className="detail-box">
        <h3 className="section-title">Complaint Details</h3>

        <div className="detail-info">
          <div className="detail-row"><span className="detail-label">Sr. No:</span>      <span className="detail-value">{complaint.id}</span></div>
          <div className="detail-row"><span className="detail-label">Level:</span>       <span className="detail-value"><span className="level-badge">{complaint.level}</span></span></div>
          <div className="detail-row">
            <span className="detail-label">Status:</span>
            <span className={`detail-value status-badge status-${complaint.status.replace(' ','-').toLowerCase()}`}>{complaint.status}</span>
          </div>
          <div className="detail-row"><span className="detail-label">First Receipt:</span><span className="detail-value">{new Date(complaint.firstReceiptDate).toLocaleDateString()}</span></div>

          <div className="detail-row timeline-trigger-row">
            <button className="history-timeline-btn" onClick={() => setShowTimeline(true)} type="button">🕐 History Timeline</button>
          </div>

          <div className="detail-row"><span className="detail-label">Last Marked:</span><span className="detail-value">{complaint.lastMarkedDate ? new Date(complaint.lastMarkedDate).toLocaleString() : 'N/A'}</span></div>
          <div className="detail-row"><span className="detail-label">Name:</span>        <span className="detail-value">{complaint.name}</span></div>
          <div className="detail-row"><span className="detail-label">Mobile:</span>      <span className="detail-value">{complaint.mobile}</span></div>
          <div className="detail-row"><span className="detail-label">Current PS:</span>  <span className="detail-value">{complaint.currentPS}</span></div>

          <div className="detail-row">
            <span className="detail-label">🔍 Enquiry Officer:</span>
            <span className="detail-value">{complaint.enquiryOfficerName || '—'}</span>
          </div>

          {(complaint.forwarded_from_level || complaint.forwardedFromLevel) && (
            <div className="detail-row forwarded-info-row">
              <span className="detail-label">↗️ Forwarded From:</span>
              <span className="detail-value forwarded-value">
                {complaint.forwarded_from_level || complaint.forwardedFromLevel}
                {(complaint.previous_ps || complaint.previousPS) && ` - ${complaint.previous_ps || complaint.previousPS}`}
              </span>
            </div>
          )}
        </div>

        <div className="description-section">
          <div className="description-label">Description:</div>
          <div className="description-content">{complaint.description}</div>
        </div>

        <div className="update-form">
          <div className="form-group">
            <label>Update Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-select">
              <option value="Pending">Pending</option>
              <option value="Satisfied">Satisfied</option>
              <option value="Not Satisfied">Not Satisfied</option>
              <option value="Miscellaneous">Miscellaneous</option>
            </select>
          </div>

          {status === 'Not Satisfied' && higherLevels.length > 0 && (
            <div className="escalation-section">
              <div className="form-group">
                <label>Escalate to higher level</label>
                <select value={escalateTo} onChange={(e) => { setEscalateTo(e.target.value); setEscalatePS(''); }} className="form-select">
                  <option value="">-- Select level to escalate --</option>
                  {higherLevels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              {escalateTo && (
                <div className="form-group">
                  <label>Assign officer / PS at {escalateTo}</label>
                  <select value={escalatePS} onChange={(e) => setEscalatePS(e.target.value)} className="form-select">
                    <option value="">-- Select --</option>
                    {escalateStations.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Remarks</label>
            <div className="textarea-wrapper">
              <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="form-textarea" rows={3} placeholder="Enter remarks..." />
              <button type="button" className="textarea-expand-btn" title="Expand to read" onClick={() => setShowRemarksExpand(true)}>⛶</button>
            </div>
          </div>

          <button className="btn-save" onClick={save}>Save Changes</button>
        </div>

        {complaint.history && complaint.history.length > 0 && (
          <div className="history-section">
            <div className="history-title">History</div>
            <div className="history-list">
              {complaint.history.map((h, idx) => (
                <div key={idx} className="history-item">
                  <div className="history-date">{new Date(h.created_at).toLocaleString('en-IN')}</div>
                  <div className="history-action">{h.action}{h.action_by&&` by ${h.action_by}`}{h.from_ps&&` (from ${h.from_ps})`}{h.to_ps&&` → ${h.to_ps}`}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showTimeline && (
        <HistoryTimelinePopup
          complaint={complaint}
          statusHistory={complaint.statusHistory || []}
          onClose={() => setShowTimeline(false)}
        />
      )}
      {showRemarksExpand && (
        <RemarksExpandPopup title="Remarks" value={remarks} onClose={() => setShowRemarksExpand(false)} />
      )}
    </>
  );
}

export default Dashboard;